import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

/* -----------------------------
  Helpers
----------------------------- */
function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

function Icon({ name, className = "h-5 w-5" }) {
  switch (name) {
    case "arrowLeft":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M15 18 9 12l6-6"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "upload":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3v10m0 0 4-4m-4 4-4-4"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 17v3h16v-3"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      );
    case "shield":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3Zm-1 14-3-3 1.4-1.4L11 13.2l3.6-3.6L16 11l-5 5Z"
            fill="currentColor"
          />
        </svg>
      );
    case "clock":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Zm1-10V7h-2v6l4 2 1-1.7-3-1.3Z"
            fill="currentColor"
          />
        </svg>
      );
    case "doc":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M7 2h7l4 4v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm7 1v4h4"
            fill="currentColor"
            opacity="0.95"
          />
          <path
            d="M8 11h8M8 15h8M8 19h6"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            opacity="0.9"
          />
        </svg>
      );
    case "spark":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2l1.2 4.2L17 7.5l-3.8 1.3L12 13l-1.2-4.2L7 7.5l3.8-1.3L12 2ZM19 12l.8 2.7L22 15.5l-2.2.8L19 19l-.8-2.7L16 15.5l2.2-.8L19 12Z"
            fill="currentColor"
          />
        </svg>
      );
    default:
      return null;
  }
}

/* -----------------------------
  Mock data (appointments + forms)
----------------------------- */
const MOCK_APPOINTMENTS = [
  {
    id: "a1",
    label: "Physiotherapy • Toronto, ON • Tomorrow 10:30 AM",
    doctorName: "Dr. Emily Carter",
    doctorSpecialty: "Physiotherapy",
    clinic: "OrbisCare Physio & Rehab",
    location: "Downtown Toronto, ON",
    dateTime: "Tomorrow • 10:30 AM",
  },
  {
    id: "a2",
    label: "Dentistry • Vancouver, BC • Tomorrow 4:30 PM",
    doctorName: "Dr. Liam Thompson",
    doctorSpecialty: "Dentistry",
    clinic: "BrightSmile Dental Studio",
    location: "Kitsilano, Vancouver, BC",
    dateTime: "Tomorrow • 4:30 PM",
  },
  {
    id: "a3",
    label: "Cardiology • Calgary, AB • Next Fri 11:00 AM",
    doctorName: "Dr. Ava Singh",
    doctorSpecialty: "Cardiology",
    clinic: "HeartFirst Cardiology Clinic",
    location: "Beltline, Calgary, AB",
    dateTime: "Next Fri • 11:00 AM",
  },
];


const INSURANCE_FORMS = [
  { id: "f_star", name: "Star Health - Claim Form" },
  { id: "f_hdfc", name: "HDFC ERGO - Reimbursement Form" },
  { id: "f_icici", name: "ICICI Lombard - Medical Certificate" },
  { id: "f_care", name: "Care Health - OPD Claim Form" },
  { id: "f_niva", name: "Niva Bupa - Consultation Claim" },
  { id: "f_other", name: "Other / Custom Form" },
];

/* -----------------------------
  UI components
----------------------------- */
function Card({ title, right, children }) {
  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div className="font-semibold">{title}</div>
        {right}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <label className="text-xs text-muted">{label}</label>
        {hint ? <span className="text-xs text-muted">{hint}</span> : null}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function TimelineItem({ step, title, desc }) {
  return (
    <div className="flex gap-3 rounded-2xl border border-border bg-white p-4">
      <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
        {step}
      </div>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="mt-1 text-sm text-muted">{desc}</div>
      </div>
    </div>
  );
}

/* -----------------------------
  Page
----------------------------- */
export default function InsuranceSubmit() {
  const navigate = useNavigate();

  const [appointmentId, setAppointmentId] = useState(MOCK_APPOINTMENTS[0].id);
  const [formId, setFormId] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]); // File[] (optional)

  const appointment = useMemo(() => {
    return MOCK_APPOINTMENTS.find((a) => a.id === appointmentId) || MOCK_APPOINTMENTS[0];
  }, [appointmentId]);

  const selectedForm = useMemo(() => {
    return INSURANCE_FORMS.find((f) => f.id === formId) || null;
  }, [formId]);

  const canSubmit = Boolean(appointmentId && formId);

  function onPickFiles(ev) {
    const picked = Array.from(ev.target.files || []);
    setFiles((prev) => [...prev, ...picked].slice(0, 5)); // cap 5
    ev.target.value = "";
  }

  function removeFile(idx) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  function onSubmit() {
    if (!canSubmit) return;

    // Later: POST to backend
    // payload: appointmentId, formId, notes, files
    alert(
      `Submitted (mock)\n\nAppointment: ${appointment.label}\nDoctor: ${appointment.doctorName}\nForm: ${
        selectedForm?.name
      }\nFiles: ${files.length}`
    );

    navigate("/patient/insurance");
  }

  return (
    <div className="w-full">
      {/* Top header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-primary">
            <span className="h-9 w-9 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Icon name="spark" className="h-5 w-5" />
            </span>
            <h1 className="text-2xl font-semibold text-text">Submit Insurance Form</h1>
          </div>
          <p className="mt-2 text-sm text-muted">
            Select an appointment, choose your insurance form, attach documents (optional), and send to the doctor.
          </p>
        </div>

        <Link
          to="/patient/insurance"
          className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-50"
        >
          <Icon name="arrowLeft" />
          Back to Insurance
        </Link>
      </div>

      {/* Fancy info strip */}
      <div className="mt-4 rounded-3xl border border-border bg-surface shadow-soft overflow-hidden">
        <div
          className="px-6 py-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(var(--c-primary),0.10), rgba(var(--c-accent),0.10), rgba(255,255,255,0.55))",
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-2xl bg-white/80 border border-border flex items-center justify-center text-primary">
                <Icon name="shield" />
              </span>
              <div>
                <div className="text-sm font-semibold">Secure & Private</div>
                <div className="text-xs text-muted">
                  Your files are shared only with the doctor for this appointment.
                </div>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 text-xs text-muted">
              <Icon name="clock" className="h-4 w-4" />
              Average doctor turnaround: <span className="font-semibold text-text">24–48 hours</span>
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="mt-5 grid grid-cols-12 gap-5">
        {/* Left form */}
        <div className="col-span-12 lg:col-span-7 space-y-5">
          <Card title="Form Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Select Appointment" hint="Doctor auto-fills from appointment">
                <select
                  value={appointmentId}
                  onChange={(e) => setAppointmentId(e.target.value)}
                  className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {MOCK_APPOINTMENTS.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Insurance Form Template" hint="Required">
                <select
                  value={formId}
                  onChange={(e) => setFormId(e.target.value)}
                  className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">Select a form</option>
                  {INSURANCE_FORMS.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {/* Auto-filled doctor info */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <InfoBox label="Doctor" value={appointment.doctorName} />
              <InfoBox label="Specialty" value={appointment.doctorSpecialty} />
              <InfoBox label="Appointment" value={appointment.dateTime} />
              <InfoBox label="Clinic" value={appointment.clinic} className="md:col-span-2" />
              <InfoBox label="Location" value={appointment.location} />
            </div>

            <div className="mt-4">
              <Field label="Notes to Doctor (optional)" hint="Mention claim type, policy number, etc.">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full min-h-[120px] rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Example: Please fill OPD reimbursement section. Policy no: XXXX. Attachments include invoice + prescription."
                />
              </Field>
            </div>
          </Card>

          <Card
            title="Attachments (optional)"
            right={<span className="text-xs text-muted">PDF/JPG/PNG • up to 5 files</span>}
          >
            <div className="rounded-2xl border border-dashed border-border bg-white p-6 text-center">
              <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                <Icon name="upload" className="h-6 w-6" />
              </div>
              <div className="mt-3 text-sm font-semibold">Upload supporting documents</div>
              <div className="mt-1 text-xs text-muted">
                Invoice, prescription, discharge summary, lab reports (if any).
              </div>

              <div className="mt-4">
                <label className="btn-primary-gradient inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white cursor-pointer">
                  <Icon name="doc" className="h-5 w-5" />
                  Choose files
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    multiple
                    className="hidden"
                    onChange={onPickFiles}
                  />
                </label>
              </div>
            </div>

            {files.length > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {files.map((f, idx) => (
                  <div
                    key={`${f.name}-${idx}`}
                    className="rounded-2xl border border-border bg-white px-4 py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{f.name}</div>
                      <div className="mt-1 text-xs text-muted">
                        {(f.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(idx)}
                      className="rounded-xl border border-border bg-slate-50 px-3 py-2 text-xs font-semibold hover:bg-slate-100"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Right panel: timeline + submit */}
        <div className="col-span-12 lg:col-span-5 space-y-5">
          <Card title="What happens next">
            <div className="space-y-3">
              <TimelineItem
                step="1"
                title="We send your form to the doctor"
                desc="Doctor receives the form linked to this appointment."
              />
              <TimelineItem
                step="2"
                title="Doctor fills required sections"
                desc="If anything is missing, you’ll see ‘Needs More Info’ in insurance listing."
              />
              <TimelineItem
                step="3"
                title="You download the signed copy"
                desc="Once complete, status becomes ‘Signed & Ready’ and a download button appears."
              />
            </div>

            <div className="mt-4 rounded-2xl border border-border bg-white p-4 text-xs text-muted">
              Tip: Include invoice & prescription for faster processing.
            </div>
          </Card>

          <Card title="Review & Submit">
            <div className="rounded-2xl border border-border bg-white p-4">
              <div className="text-xs text-muted">Appointment</div>
              <div className="mt-1 text-sm font-semibold">{appointment.label}</div>

              <div className="mt-3 text-xs text-muted">Doctor</div>
              <div className="mt-1 text-sm font-semibold">{appointment.doctorName}</div>

              <div className="mt-3 text-xs text-muted">Selected Form</div>
              <div className="mt-1 text-sm font-semibold">
                {selectedForm ? selectedForm.name : "—"}
              </div>

              <div className="mt-3 text-xs text-muted">Attachments</div>
              <div className="mt-1 text-sm font-semibold">{files.length} file(s)</div>
            </div>

            <button
              onClick={onSubmit}
              disabled={!canSubmit}
              className="mt-4 w-full btn-primary-gradient px-6 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              Send to Doctor
            </button>

            {!canSubmit && (
              <div className="mt-2 text-xs text-muted">
                Please select an <span className="font-semibold text-text">appointment</span> and an{" "}
                <span className="font-semibold text-text">insurance form</span>.
              </div>
            )}

            <div className="mt-3 flex items-center gap-2 text-xs text-muted">
              <span className="text-primary">
                <Icon name="shield" className="h-4 w-4" />
              </span>
              We only share this with the assigned doctor for your selected appointment.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function InfoBox({ label, value, className }) {
  return (
    <div className={classNames("rounded-2xl border border-border bg-white px-4 py-3", className)}>
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-1 text-sm font-semibold text-text truncate">{value}</div>
    </div>
  );
}
